<template>
    <b-modal
        id="ptvStatsModal"
        centered
        hide-footer
        :size="lidarFlag ? 'lg' : 'xl'"
        header-class="modal-header-custom"
        body-class="modal-body-custom"
    >
        <!-- Custom header -->
        <template #modal-header="{ close }">
            <div class="modal-title-wrap">
                <span class="modal-title-text">Statistic Report</span>
            </div>
            <button class="modal-close-btn" @click="close()">
                <fa :icon="['fas', 'times']" />
            </button>
        </template>

        <!-- Loading -->
        <div class="loading-wrap" v-if="dataLoading">
            <span class="spinner-border spinner-border-sm me-2" style="color:#4A74A8"></span>
            <span style="color:#4A74A8; font-size:0.9rem;">Generating Statistics...</span>
        </div>

        <div v-else-if="row">
            <!-- Summary strip -->
            <div class="summary-strip">
                <!-- Row 1: Region · ID · OSM · Total -->
                <div class="summary-row">
                    <div class="summary-item">
                        <span class="summary-label">Region</span>
                        <span class="summary-value">{{ row.region || '—' }}</span>
                    </div>
                    <div class="summary-divider"></div>
                    <div class="summary-item">
                        <span class="summary-label">ID</span>
                        <span class="summary-value">{{ row.id || '—' }}</span>
                    </div>
                    <div class="summary-divider"></div>
                    <div class="summary-item">
                        <span class="summary-label">OSM Segments Length</span>
                        <span class="summary-value accent">{{ row['osm_length'] || '0.0 km' }}</span>
                    </div>
                    <div class="summary-divider"></div>
                    <div class="summary-item">
                        <span class="summary-label">Total Segments Length</span>
                        <span class="summary-value accent">{{ lidarFlag ? row['trails_length'] || '0.0' : row['segment_length'] || '0.0' }}</span>
                    </div>
                </div>
                <!-- Row 2: Note -->
                <div class="summary-row summary-row-note">
                    <div class="summary-item summary-item-full">
                        <span class="summary-label">Note</span>
                        <span class="summary-value">{{ row.note || '—' }}</span>
                    </div>
                </div>
            </div>

            <!-- Stats grid -->
            <div :class="lidarFlag ? 'stats-grid-trt' : 'stats-grid-dd'">

                <!-- FA Metrics -->
                <div class="stat-section" v-if="!lidarFlag">
                    <div class="section-header">
                        <fa :icon="['fas', 'road']" class="me-2" />
                            <span class="ml-2"> FA Metrics</span>
                    </div>
                    <div class="stat-row">
                        <span class="stat-name">Active Construction</span>
                        <span class="stat-val">{{ row['fa_active_construction_length'] || '0.0 km' }}</span>
                    </div>
                    <div class="stat-row">
                        <span class="stat-name">Compromised Road Condition</span>
                        <span class="stat-val">{{ row['fa_compromised_road_condition_length'] || '0.0 km' }}</span>
                    </div>
                    <div class="stat-row">
                        <span class="stat-name">Construction Completed (NA)</span>
                        <span class="stat-val">{{ row['fa_construction_completed_not_acquired_length'] || '0.0 km' }}</span>
                    </div>
                    <div class="stat-row">
                        <span class="stat-name">Dropped Segments</span>
                        <span class="stat-val">{{ row['dropped_segment_length'] || '0.0 km' }}</span>
                    </div>
                </div>

                <!-- OSM Road Classes -->
                <div class="stat-section">
                    <div class="section-header">
                        <fa :icon="['fas', 'map']" class="me-2" />
                         <span class="ml-2"> OSM Road Classes</span>
                    </div>
                    <div class="stat-row" v-for="item in lidarFlag ? this.osmRows.slice(0, 8) : this.osmRows" :key="item.label">
                        <span class="stat-name">{{ item.label }}</span>
                        <span class="stat-val">{{ row[item.field] || '0.0 km' }}</span>
                    </div>
                </div>

                <!-- Road Types -->
                <div class="stat-section" v-if="!lidarFlag">
                    <div class="section-header">
                        <fa :icon="['fas', 'route']" class="me-2" /> <span class="ml-2"> Road Types</span>
                    </div>
                    <div class="stat-row">
                        <span class="stat-name">Access Ramp</span>
                        <span class="stat-val">{{ row['ramp_length'] || '0.0 km' }}</span>
                    </div>
                    <div class="stat-row">
                        <span class="stat-name">Local</span>
                        <span class="stat-val">{{ row['local_length'] || '0.0 km' }}</span>
                    </div>
                    <div class="stat-row">
                        <span class="stat-name">Interchange</span>
                        <span class="stat-val">{{ row['interchange_length'] || '0.0 km' }}</span>
                    </div>
                    <div class="stat-row">
                        <span class="stat-name">Non Controlled</span>
                        <span class="stat-val">{{ row['non_controlled_length'] || '0.0 km' }}</span>
                    </div>
                    <div class="stat-row">
                        <span class="stat-name">Controlled</span>
                        <span class="stat-val">{{ row['controlled_length'] || '0.0 km' }}</span>
                    </div>
                    <div class="stat-row">
                        <span class="stat-name">Roundabout</span>
                        <span class="stat-val">{{ row['roundabout_length'] || '0.0 km' }}</span>
                    </div>
                    <div class="stat-row">
                        <span class="stat-name">Turn Maneuver</span>
                        <span class="stat-val">{{ row['turn_maneuver_length'] || '0.0 km' }}</span>
                    </div>
                </div>

                <!-- Counts -->
                <div class="stat-section" v-if="!lidarFlag">
                    <div class="section-header">
                        <fa :icon="['fas', 'hashtag']" class="me-2" /> <span class="ml-2"> Counts</span>
                    </div>
                    <div class="stat-row">
                        <span class="stat-name">Split & Merges</span>
                        <span class="stat-val">{{ row['merges_splits_count'] || '0' }}</span>
                    </div>
                    <div class="stat-row">
                        <span class="stat-name">Intersections</span>
                        <span class="stat-val">{{ row['intersection_count'] || '0' }}</span>
                    </div>
                </div>

            </div>
        </div>
    </b-modal>
</template>

<style>
#ptvStatsModal.modal-lg {
    max-width: 50rem !important;
}
/* Modal shell */
#ptvStatsModal .modal-content {
    border: none;
    border-radius: 5px;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(6, 60, 109, 0.18);
}

#ptvStatsModal .modal-header-custom {
    color: #fff;
    /* border-bottom: 2px solid #4A74A8;
    padding: 0.8rem 1.25rem; */
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #063c6d;
}

#ptvStatsModal .modal-body-custom {
    padding: 0;
    background: #f8f9fa;
}

/* Header */
.modal-title-wrap {
    display: flex;
    align-items: center;
    gap: 0.55rem;
}

.modal-title-dot {
    display: inline-block;
    width: 0.625rem;
    height: 0.625rem;
    border-radius: 50%;
    background: #4A74A8;
    flex-shrink: 0;
}

.modal-title-text {
    font-size: 0.95rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
}

.modal-close-btn {
    background: none;
    border: none;
    color: #adb5bd;
    font-size: 1rem;
    cursor: pointer;
    line-height: 1;
    padding: 0;
    transition: color 0.15s ease;
}

.modal-close-btn:hover {
    color: #0c78ff;
}

/* Loading */
.loading-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2.5rem;
}

/* Summary strip */
.summary-strip {
    display: flex;
    flex-direction: column;
    background: #fff;
    border-bottom: 1px solid #e9ecef;
    padding: 0.75rem 1.25rem;
    gap: 0;
}

.summary-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}

.summary-row-note {
    border-top: 1px solid #f0f2f5;
    padding-top: 0.55rem;
    margin-top: 0.55rem;
}

.summary-item {
    display: flex;
    flex-direction: column;
    padding: 0 1.1rem;
}

.summary-item:first-child {
    padding-left: 0;
}

.summary-item-full {
    flex: 1;
    padding-left: 0;
}

.summary-label {
    font-size: 0.9rem;
    font-weight: 600;
    color: #27282a;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.1rem;
}

.summary-value {
    font-size: 0.98rem;
    font-weight: 600;
    color: #344767;
}

.summary-value.accent {
    color: #0c78ff;
}

.summary-divider {
    width: 1px;
    height: 2rem;
    background: #e9ecef;
    flex-shrink: 0;
}

/* Stats grid */
.stats-grid-dd {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0;
    padding: 1rem;
    gap: 0.75rem;
}

.stats-grid-trt {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 0;
    padding: 1rem;
    gap: 0.75rem;
}

.stat-section {
    background: #fff;
    border-radius: 10px;
    border: 1px solid #e9ecef;
    overflow: hidden;
}

.section-header {
    background: #f0f4f9;
    border-bottom: 1px solid #dee2e6;
    color: #063c6d;
    font-size: 0.9rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 0.55rem 0.85rem;
}

.stat-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.45rem 0.85rem;
    border-bottom: 1px solid #f0f2f5;
    transition: background 0.1s ease;
}

.stat-row:last-child {
    border-bottom: none;
}

.stat-row:hover {
    background: #f8fafc;
}

.stat-name {
    font-size: 0.9rem;
    color: #063c6d;
    flex: 1;
    padding-right: 0.5rem;
}

.stat-val {
    font-size: 0.9rem;
    font-weight: 600;
    color:  #0c78ff;
    white-space: nowrap;
}
</style>

<script>
import axios from 'axios'
export default {
    name: 'DT_StatsModal',
    data() {
        return {
            requestName: null,
            dataLoading: false,
            osmRows: [
                { label: 'Motorway', field: 'osm_motorway_length' },
                { label: 'Motorway Link', field: 'osm_motorway_link_length' },
                { label: 'Trunk', field: 'osm_trunk_length' },
                { label: 'Trunk Link', field: 'osm_trunk_link_length' },
                { label: 'Primary', field: 'osm_primary_length' },
                { label: 'Primary Link', field: 'osm_primary_link_length' },
                { label: 'Secondary', field: 'osm_secondary_length' },
                { label: 'Secondary Link', field: 'osm_secondary_link_length' },
                { label: 'Tertiary', field: 'osm_tertiary_length' },
                { label: 'Tertiary Link', field: 'osm_tertiary_link_length' },
                { label: 'Residential', field: 'osm_residential_length' },
                { label: 'Service', field: 'osm_service_length' },
                { label: 'Unclassified', field: 'osm_unclassified_length' },
            ]
        }
    },
    props: ['polygonWkt', 'selectedExtent', 'user', 'row', 'lidarFlag'],
    methods: {

    }
}
</script>
